import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  DialogContent,
  Typography,
  DialogActions,
  DialogTitle,
  Dialog,
} from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "../../config/axios";
import { useGooglePicker } from "../../hooks/useGooglePicker";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { formatInTimeZone } from "date-fns-tz";

function AddProduct() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState([
    { mfg_date: "", lot_number: "", tempId: uuidv4() },
  ]);
  const [mainFormData, setMainFormData] = useState({ name: "", brand: "" });
  const [metaTags, setMetaTags] = useState([]);
  const [threeField, setThreeField] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [threeFieldInput, setThreeFieldInput] = useState("");
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const dialogContentRef = useRef(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [indexToRemove, setIndexToRemove] = useState(null);
  const [activePickerTempId, setActivePickerTempId] = useState(null);

  const {
    gapiLoaded,
    pickerLoading,
    apiError: pickerApiError,
    createPicker,
  } = useGooglePicker({
    onFilePicked: (file, tempId) => {
      setSelectedFiles((prev) => {
        if (!prev.some((f) => f.id === file.id && f.tempId === tempId)) {
          return [...prev, { ...file, tempId }];
        }
        return prev.map((f) =>
          f.id === file.id && f.tempId === tempId ? { ...f, tempId } : f
        );
      });
      setActivePickerTempId(null);

      handleFileAssignToLot(file, tempId);
    },
  });

  useEffect(() => {
    if (pickerApiError) {
      setApiError(pickerApiError);
    }
  }, [pickerApiError]);

  const removeFile = (fileId, tempId) => {
    setSelectedFiles((prev) =>
      prev.filter((file) => file.id !== fileId || file.tempId !== tempId)
    );
  };

  const getSectionFiles = (tempId) =>
    selectedFiles.filter((file) => file.tempId === tempId);

  const handleMainInputChange = (e) => {
    const { name, value } = e.target;
    setMainFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    if (name === "lot_number") {
      const manufacturingDate = decodeTheLotNumber(value);
      setFormData((prev) =>
        prev.map((data, i) =>
          i === index
            ? { ...data, lot_number: value, mfg_date: manufacturingDate }
            : data
        )
      );
      if (manufacturingDate && errors[`mfg_date_${index}`]) {
        setErrors((prev) => ({ ...prev, [`mfg_date_${index}`]: "" }));
      }
    } else {
      setFormData((prev) =>
        prev.map((data, i) => (i === index ? { ...data, [name]: value } : data))
      );
    }
    if (errors[`${name}_${index}`]) {
      setErrors((prev) => ({ ...prev, [`${name}_${index}`]: "" }));
    }
  };

  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };

  const handleThreeFieldInputChange = (e) => {
    setThreeFieldInput(e.target.value.toUpperCase());
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === "Enter" && tagInput.trim()) {
      e.preventDefault();
      if (!metaTags.includes(tagInput.trim())) {
        setMetaTags((prev) => [...prev, tagInput.trim()]);
        setTagInput("");
      }
    }
  };

  const handleThreeFieldInputKeyDown = (e) => {
    if (e.key === "Enter" && threeFieldInput.trim()) {
      e.preventDefault();
      if (!threeField.includes(threeFieldInput.trim())) {
        setThreeField((prev) => [...prev, threeFieldInput.trim()]);
        setThreeFieldInput("");
      }
    }
  };

  const handleTagDelete = (tagToDelete) => {
    setMetaTags((prev) => prev.filter((tag) => tag !== tagToDelete));
  };

  const handleThreeFieldDelete = (fieldToDelete) => {
    setThreeField((prev) => prev.filter((bar) => bar !== fieldToDelete));
  };

  const decodeTheLotNumber = (lotNumber) => {
    if (!/^\d{5,9}$/.test(lotNumber)) return "";

    const dayOfYear = parseInt(lotNumber.substring(0, 3), 10);
    const yearPart = parseInt(lotNumber.substring(3, 5), 10);
    const fullYear = 2000 + yearPart;

    // Check if year is leap
    const isLeap =
      (fullYear % 4 === 0 && fullYear % 100 !== 0) || fullYear % 400 === 0;

    // Check if dayOfYear is valid
    if (dayOfYear < 1 || dayOfYear > (isLeap ? 366 : 365)) {
      toast.error(
        `Invalid day for year ${fullYear}. ${
          isLeap
            ? "Leap year allows max 366 days."
            : "Not a leap year, max 365 are there."
        }`
      );
      return "";
    }

    const date = new Date(Date.UTC(fullYear, 0, dayOfYear));

    return date.toISOString().split("T")[0];
  };

  const handleFileAssignToLot = (file, lotId) => {
    const isDuplicate = selectedFiles.some(
      (f) => f.id === file.id && f.tempId !== lotId
    );

    if (isDuplicate) {
      toast.warning("This file is already in used.");
      return;
    }

    const lotIndex = formData.findIndex((f) => f.tempId === lotId);
    if (lotIndex !== -1) {
      const lotNumber = formData[lotIndex].lot_number;

      const fileNameNumber = file.name.replace(/\D/g, "");

      if (fileNameNumber !== lotNumber) {
        toast.warning(
          `Filename (${fileNameNumber}) does not match lot number (${lotNumber})`
        );
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const numericRegex = /^[0-9]+$/;

    if (!mainFormData.name.trim()) newErrors.name = "Product Name is required";
    if (!mainFormData.brand) newErrors.brand = "Brand is required";

    formData.forEach((data, index) => {
      if (
        !data.lot_number ||
        !numericRegex.test(data.lot_number) ||
        data.lot_number.length < 5 ||
        data.lot_number.length > 9
      ) {
        newErrors[`lot_number_${index}`] =
          "Lot Number is required, must be numeric, and between 5 to 9 digits";
        return;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setApiError(null);
      const payload = {
        ...mainFormData,
        mfg_date_lot_number: formData.map((data) => ({
          mfg_date: data.mfg_date,
          lot_number: data.lot_number,
        })),
        keyword_search_terms: metaTags.join(","),
        asin_upc_fnsku: threeField.join(","),
      };

      const response = await axios.post("/products", payload);

      if (selectedFiles.length > 0) {
        const productId = response.data.id;
        const lotMap = response.data.lots.reduce((acc, lot, index) => {
          acc[formData[index].tempId] = lot.id;
          return acc;
        }, {});

        for (const file of selectedFiles) {
          const product_lot_id = lotMap[file.tempId];
          if (!product_lot_id) {
            console.warn(`No product_lot_id found for tempId: ${file.tempId}`);
            continue;
          }
          await axios.post(`/products/${productId}/files`, {
            file_id: file.id,
            filename: file.name,
            fileUrl: file.url,
            created_at: file.createdTime,
            product_lot_id,
          });
        }
      }
      toast.success("Product created successfully");
      navigate("/products");
    } catch (error) {
      // if (error.response?.data?.message === "This lot number already exist") {
      //   toast.error("This lot number already exist");
      //   return;
      // }
      // if (
      //   error.response?.data?.message ===
      //   "A product with the same name and brand already exists"
      // ) {
      //   toast.error("A product with the same name and brand already exists");
      //   return;
      // }
      // if (
      //   error.response?.data?.message ===
      //   "A product with the same asin / upc / fnsku already exists"
      // ) {
      //   toast.error(
      //     "A product with the same asin / upc / fnsku already exists"
      //   );
      //   return;
      // }
      toast.error(error.response?.data?.message);
      setApiError(error.response?.data?.message || "Error adding product");
    } finally {
      setLoading(false);
    }
  };

  const openGooglePicker = (tempId) => {
    if (gapiLoaded && !pickerLoading && dialogContentRef.current) {
      setActivePickerTempId(tempId);
      createPicker(dialogContentRef, tempId, () => {
        setActivePickerTempId(null);
      });
    }
  };

  useEffect(() => {
    if (apiError) {
      setTimeout(() => {
        setApiError("");
      }, 3000);
    }
  }, [apiError]);

  const handleAddFields = () => {
    setFormData((prev) => [
      ...prev,
      { mfg_date: "", lot_number: "", tempId: uuidv4() },
    ]);
  };

  const handleRemoveFields = (index) => {
    const hasFilledFields =
      formData[index].mfg_date ||
      formData[index].lot_number ||
      getSectionFiles(formData[index].tempId).length > 0;
    if (hasFilledFields) {
      setDeleteDialogOpen(true);
      setIndexToRemove(index);
    } else {
      setFormData((prev) => prev.filter((_, i) => i !== index));
      setSelectedFiles((prev) =>
        prev.filter((file) => file.tempId !== formData[index].tempId)
      );
    }
  };

  const pacificDate = formatInTimeZone(
    new Date(),
    "America/Los_Angeles",
    "yyyy-MM-dd"
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        padding: "20px",
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "750px",
          background: "#FFFFFF",
          borderRadius: "12px",
          padding: "30px",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
          animation: "fadeIn 0.5s ease-in-out",
        }}
      >
        <h1
          style={{
            color: "#263238",
            fontSize: "28px",
            fontWeight: 600,
            marginBottom: "20px",
            marginTop: "-10px",
            textAlign: "center",
          }}
        >
          Add Product
        </h1>

        {/* {apiError && (
          <div
            style={{
              background: "#FF6F61",
              color: "#FFFFFF",
              padding: "12px",
              borderRadius: "8px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              animation: "slideIn 0.3s ease-in-out",
            }}
          >
            <ErrorIcon style={{ marginRight: "8px" }} />
            {apiError}
          </div>
        )} */}

        <form onSubmit={handleSubmit} ref={dialogContentRef}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            <TextField
              label="Brand"
              name="brand"
              value={mainFormData.brand}
              onChange={handleMainInputChange}
              error={!!errors.brand}
              helperText={
                errors.brand && (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      color: "#FF6F61",
                    }}
                  >
                    <ErrorIcon
                      style={{ fontSize: "16px", marginRight: "4px" }}
                    />
                    {errors.brand}
                  </span>
                )
              }
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "8px",
                  background: "#F5F5F5",
                  transition: "all 0.3s",
                },
                "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#26A69A",
                  boxShadow: "0 0 8px rgba(38, 166, 154, 0.3)",
                },
                "& .MuiInputLabel-root": { color: "#263238", fontWeight: 500 },
                "& .MuiInputLabel-root.Mui-focused": { color: "#26A69A" },
              }}
            />
            <TextField
              label="Product Name"
              name="name"
              value={mainFormData.name}
              onChange={handleMainInputChange}
              error={!!errors.name}
              helperText={
                errors.name && (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      color: "#FF6F61",
                    }}
                  >
                    <ErrorIcon
                      style={{ fontSize: "16px", marginRight: "4px" }}
                    />
                    {errors.name}
                  </span>
                )
              }
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "8px",
                  background: "#F5F5F5",
                  transition: "all 0.3s",
                },
                "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#26A69A",
                  boxShadow: "0 0 8px rgba(38, 166, 154, 0.3)",
                },
                "& .MuiInputLabel-root": { color: "#263238", fontWeight: 500 },
                "& .MuiInputLabel-root.Mui-focused": { color: "#26A69A" },
              }}
            />
            <div style={{ gridColumn: "span 2", marginTop: "-15px" }}>
              <h3
                style={{
                  color: "#263238",
                  fontSize: "18px",
                  fontWeight: 500,
                  marginBottom: "0px",
                }}
              >
                ASIN / UPC / FNSKU
              </h3>
              {threeField.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginBottom: "3px",
                    background: "#F5F5F5",
                    borderRadius: "8px",
                    padding: "12px",
                  }}
                >
                  {threeField.map((bar) => (
                    <Chip
                      key={bar}
                      label={bar}
                      onDelete={() => handleThreeFieldDelete(bar)}
                      style={{
                        background: "#26A69A",
                        color: "#FFFFFF",
                        transform: "scale(1)",
                        transition: "transform 0.2s",
                      }}
                      sx={{ "&:hover": { transform: "scale(1.05)" } }}
                    />
                  ))}
                </div>
              )}
              <TextField
                fullWidth
                label="Add (press Enter)"
                value={threeFieldInput.toUpperCase()}
                onChange={handleThreeFieldInputChange}
                onKeyDown={handleThreeFieldInputKeyDown}
                sx={{
                  "& .MuiInputBase-root": {
                    borderRadius: "8px",
                    background: "#F5F5F5",
                    transition: "all 0.3s",
                  },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#26A69A",
                    boxShadow: "0 0 8px rgba(38, 166, 154, 0.3)",
                  },
                  "& .MuiInputLabel-root": {
                    color: "#263238",
                    fontWeight: 500,
                  },
                  "& .MuiInputLabel-root.Mui-focused": { color: "#26A69A" },
                }}
              />
            </div>
            <div style={{ gridColumn: "span 2", marginTop: "-15px" }}>
              <h3
                style={{
                  color: "#263238",
                  fontSize: "18px",
                  fontWeight: 500,
                  marginBottom: "0px",
                }}
              >
                Keyword Search Terms
              </h3>
              {metaTags.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginBottom: "3px",
                    background: "#F5F5F5",
                    borderRadius: "8px",
                    padding: "12px",
                  }}
                >
                  {metaTags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleTagDelete(tag)}
                      style={{
                        background: "#26A69A",
                        color: "#FFFFFF",
                        transform: "scale(1)",
                        transition: "transform 0.2s",
                      }}
                      sx={{ "&:hover": { transform: "scale(1.05)" } }}
                    />
                  ))}
                </div>
              )}
              <TextField
                fullWidth
                label="Add tags (press Enter)"
                value={tagInput}
                onChange={handleTagInputChange}
                onKeyDown={handleTagInputKeyDown}
                sx={{
                  "& .MuiInputBase-root": {
                    borderRadius: "8px",
                    background: "#F5F5F5",
                    transition: "all 0.3s",
                  },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#26A69A",
                    boxShadow: "0 0 8px rgba(38, 166, 154, 0.3)",
                  },
                  "& .MuiInputLabel-root": {
                    color: "#263238",
                    fontWeight: 500,
                  },
                  "& .MuiInputLabel-root.Mui-focused": { color: "#26A69A" },
                }}
              />
              <Box
                sx={{
                  minWidth: "200px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexShrink: 0,
                  flexWrap: "wrap",
                  gap: "16px",
                }}
              >
                <Button
                  onClick={handleAddFields}
                  sx={{
                    background: "#26A69A",
                    color: "#FFFFFF",
                    borderRadius: "12px",
                    fontWeight: 600,
                    textTransform: "none",
                    padding: "12px 24px",
                    marginTop: "16px",
                    fontSize: "16px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    transition: "all 0.3s",
                    "&:hover": {
                      backgroundColor: "#219187",
                    },
                    "@media (max-width: 600px)": {
                      padding: "10px 20px",
                      fontSize: "14px",
                    },
                  }}
                >
                  + Add New Lot
                </Button>
                <div
                  style={{
                    gridColumn: "span 2",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "16px",
                    marginTop: "24px",
                  }}
                >
                  <Button
                    onClick={() => navigate("/products")}
                    style={{
                      background: "#FFFFFF",
                      color: "#263238",
                      padding: "10px 20px",
                      borderRadius: "8px",
                      border: "2px solid #263238",
                      fontWeight: 500,
                      textTransform: "none",
                      transition: "all 0.3s",
                    }}
                    sx={{
                      "&:hover": {
                        background: "#263238",
                        color: "#FFFFFF",
                        transform: "scale(1.02)",
                      },
                      "@media (max-width: 600px)": {
                        width: "90%",
                        marginBottom: "8px",
                        justifyContent: "center",
                      },
                      "@media (max-width: 320px)": {
                        width: "50%",
                        marginBottom: "8px",
                        display: "flex",
                        justifyContent: "center",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    style={{
                      background: "#FF6F61",
                      color: "#FFFFFF",
                      padding: "10px 20px",
                      borderRadius: "8px",
                      fontWeight: 500,
                      textTransform: "none",
                      transition: "all 0.3s",
                    }}
                    sx={{
                      "&:hover": {
                        background: "#F4511E",
                        transform: "scale(1.02)",
                      },
                      "&:disabled": {
                        background: "#B0BEC5",
                        cursor: "not-allowed",
                      },
                      "@media (max-width: 600px)": {
                        width: "90%",
                        justifyContent: "center",
                      },
                      "@media (max-width: 320px)": {
                        width: "60%",
                        marginBottom: "8px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      },
                    }}
                  >
                    {loading ? "Adding..." : "Add Product"}
                  </Button>
                </div>
              </Box>
              <hr
                style={{
                  border: "none",
                  height: "2px",
                  backgroundColor: "#ccc",
                  marginTop: "20px",
                }}
              />
            </div>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "flex-start",
                width: "204%",
                maxWidth: "204%",
                overflowX: "hidden",
              }}
            >
              {formData
                .slice()
                .reverse()
                .map((data, index) => (
                  <Box
                    key={data.tempId}
                    sx={{
                      width: "100%",
                      flexShrink: 0,
                      border: "1px solid #E0E0E0",
                      borderRadius: "16px",
                      padding: "32px",
                      backgroundColor: "#FFFFFF",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
                      marginBottom: "20px",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        boxShadow: "0 6px 16px rgba(0, 0, 0, 0.08)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        marginBottom: 3,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderBottom: "2px solid #26A69A",
                          paddingBottom: "8px",
                          marginTop: "-15px",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            color: "#263238",
                            fontWeight: 600,
                          }}
                        >
                          Lot Entry {formData.length - index}
                        </Typography>

                        {index < formData.length - 1 && (
                          <IconButton
                            onClick={() =>
                              handleRemoveFields(formData.length - 1 - index)
                            }
                            sx={{
                              color: "#FF6F61",
                              transition: "transform 0.2s",
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                        gap: 2,
                        marginBottom: 3,
                      }}
                    >
                      <TextField
                        label="Lot Number"
                        type="number"
                        name="lot_number"
                        value={data.lot_number}
                        onChange={(e) =>
                          handleInputChange(e, formData.length - 1 - index)
                        }
                        error={
                          !!errors[`lot_number_${formData.length - 1 - index}`]
                        }
                        helperText={
                          errors[
                            `lot_number_${formData.length - 1 - index}`
                          ] && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                color: "#FF6F61",
                              }}
                            >
                              <ErrorIcon
                                sx={{ fontSize: "16px", marginRight: "4px" }}
                              />
                              {
                                errors[
                                  `lot_number_${formData.length - 1 - index}`
                                ]
                              }
                            </Box>
                          )
                        }
                        fullWidth
                        sx={{
                          "& .MuiInputBase-root": {
                            borderRadius: "8px",
                            backgroundColor: "#F9FAFB",
                          },
                          "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#26A69A",
                            boxShadow: "0 0 8px rgba(38, 166, 154, 0.2)",
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#26A69A",
                          },
                        }}
                      />
                      <TextField
                        label="Manufacturing Date"
                        type="date"
                        name="mfg_date"
                        value={data.mfg_date}
                        onChange={(e) =>
                          handleInputChange(e, formData.length - 1 - index)
                        }
                        inputProps={{ max: pacificDate, readonly: true }}
                        InputLabelProps={{ shrink: true }}
                        error={
                          !!errors[`mfg_date_${formData.length - 1 - index}`]
                        }
                        disabled
                        helperText={
                          errors[`mfg_date_${formData.length - 1 - index}`] && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                color: "#FF6F61",
                              }}
                            >
                              <ErrorIcon
                                sx={{ fontSize: "16px", marginRight: "4px" }}
                              />
                              {
                                errors[
                                  `mfg_date_${formData.length - 1 - index}`
                                ]
                              }
                            </Box>
                          )
                        }
                        fullWidth
                        sx={{
                          "& .MuiInputBase-root": {
                            borderRadius: "8px",
                            backgroundColor: "#F9FAFB",
                          },
                          "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#26A69A",
                            boxShadow: "0 0 8px rgba(38, 166, 154, 0.2)",
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#26A69A",
                          },
                        }}
                      />
                      <Box
                        onClick={() => {
                          if (getSectionFiles(data.tempId).length === 1) {
                            toast.warning(
                              "You cannot select more than one file"
                            );
                          }
                        }}
                        sx={{ width: "100%" }}
                      >
                        <Button
                          onClick={() => openGooglePicker(data.tempId)}
                          disabled={
                            !gapiLoaded ||
                            (pickerLoading &&
                              activePickerTempId !== data.tempId) ||
                            getSectionFiles(data.tempId).length === 1
                          }
                          fullWidth
                          sx={{
                            backgroundColor: "#26A69A",
                            color: "#FFFFFF",
                            borderRadius: "8px",
                            padding: "10px 24px",
                            textTransform: "none",
                            fontSize: "14px",
                            fontStretch: "100%",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            height: "55px",
                            maxWidth: "100%",
                            "&:hover": {
                              backgroundColor: "#219187",
                            },
                          }}
                        >
                          {pickerLoading && activePickerTempId === data.tempId
                            ? "Loading..."
                            : "Link Results"}
                        </Button>
                      </Box>
                    </Box>

                    {/* <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "#37474F", fontWeight: 600 }}
                      >
                        Upload Files for Lot {data.lot_number || index + 1}
                      </Typography>
                    </Box> */}

                    {getSectionFiles(data.tempId).length > 0 && (
                      <TableContainer
                        sx={{
                          borderRadius: "8px",
                          overflow: "hidden",
                          backgroundColor: "#F9FAFB",
                        }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{ fontWeight: 600, color: "#455A64" }}
                              >
                                File Name
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 600, color: "#455A64" }}
                              >
                                Created Time
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 600, color: "#455A64" }}
                              >
                                Actions
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {getSectionFiles(data.tempId).map((file) => (
                              <TableRow
                                key={`${file.id}-${file.tempId}`}
                                sx={{
                                  "&:hover": { backgroundColor: "#E0F2F1" },
                                  transition: "background 0.2s",
                                }}
                              >
                                <TableCell>{file.name}</TableCell>
                                <TableCell>
                                  {new Date(file.createdTime).toLocaleString(
                                    "en-GB",
                                    {
                                      day: "2-digit",
                                      month: "long",
                                      year: "numeric",
                                    }
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    size="small"
                                    sx={{
                                      color: "#FF6F61",
                                      textTransform: "none",
                                    }}
                                    onClick={() =>
                                      removeFile(file.id, file.tempId)
                                    }
                                  >
                                    Remove
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                ))}
            </Box>
          </div>
        </form>
      </div>
      <style jsx global>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideIn {
          from {
            transform: translateX(-20px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "16px",
            "@media (max-width: 960px)": { width: "80%", margin: "0 auto" },
            "@media (max-width: 600px)": {
              width: "90%",
              margin: "0 auto",
              padding: "8px",
            },
          },
        }}
      >
        <DialogTitle>
          <Typography
            variant="h6"
            sx={{
              color: "#1e40af",
              fontSize: "20px",
              fontWeight: 600,
              "@media (max-width: 600px)": { fontSize: "16px" },
            }}
          >
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body1"
            sx={{
              color: "#263238",
              "@media (max-width: 600px)": { fontSize: "14px" },
            }}
          >
            {`Are you sure you want to delete Lot ${
              formData[indexToRemove]?.lot_number || indexToRemove + 1
            } and its associated files? This action cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              background: "#FFFFFF",
              color: "#263238",
              padding: "10px 20px",
              borderRadius: "8px",
              border: "2px solid #263238",
              fontWeight: 500,
              textTransform: "none",
              transition: "all 0.3s",
              "&:hover": {
                background: "#263238",
                color: "#FFFFFF",
                transform: "scale(1.02)",
              },
              "@media (max-width: 600px)": {
                padding: "6px 12px",
                fontSize: "14px",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setFormData((prev) => prev.filter((_, i) => i !== indexToRemove));
              setSelectedFiles((prev) =>
                prev.filter(
                  (file) => file.tempId !== formData[indexToRemove].tempId
                )
              );
              setDeleteDialogOpen(false);
              setIndexToRemove(null);
            }}
            sx={{
              background: "#FF6F61",
              color: "#FFFFFF",
              padding: "10px 20px",
              borderRadius: "8px",
              fontWeight: 500,
              textTransform: "none",
              transition: "all 0.3s",
              "&:hover": { background: "#F4511E", transform: "scale(1.02)" },
              "@media (max-width: 600px)": {
                padding: "6px 12px",
                fontSize: "14px",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AddProduct;
